import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Clock, Loader2, X } from 'lucide-react';
import { DOSE_APPROVAL_VALID_DAYS } from '@bloom/shared';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { trackStoreEvent } from '../../lib/dtpAnalytics';
import { formatMoney } from '../../lib/format';
import { useDtpCartStore } from '../../store/useDtpCartStore';
import type { DoseOption, ProgramDoseChoices } from '../../types/careMessaging';
import { refillApiError } from './AutoRefillSheet';

/**
 * "Your dose" on the Orders page, and the Change dose sheet.
 *
 * Doses come from the program's ladder (services/doseChanges.ts). One step
 * up or down the patient orders straight away: the dose goes in the cart with
 * its plan picked, and checkout runs as usual. A bigger move is a request to
 * the care team; once approved it is ordered the same way. The server checks
 * the same rule at checkout, so this sheet is guidance, not the guard.
 */

export function stepLabel(option: Pick<DoseOption, 'steps' | 'direction'>): string {
  if (option.direction === 'current') return 'Your current dose';
  return `${option.steps === 1 ? 'One step' : `${option.steps} steps`} ${option.direction}`;
}

export function DoseChangePanel({ readOnly }: { readOnly: boolean }) {
  const { physicianId } = useDtpParams();
  const [open, setOpen] = useState<ProgramDoseChoices | null>(null);
  const choices = useQuery<ProgramDoseChoices[]>({
    queryKey: ['dose-choices', physicianId],
    retry: false,
    queryFn: async () => {
      const res = await dtpApi.getDoseChoices();
      return res.success && res.data ? res.data : [];
    },
  });
  const programs: ProgramDoseChoices[] = choices.data || [];
  if (!programs.length) return null;

  return (
    <section aria-label="Your dose" className="mt-8 space-y-3">
      {programs.map(program => (
        <div key={program.programId} className="portal-card flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="min-w-0">
            <p className="text-sm text-[var(--portal-muted)]">{program.programName}</p>
            <p className="portal-display text-xl font-semibold">Your dose: {program.current.doseLabel}</p>
            {program.pending && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-[var(--portal-muted)]">
                <Clock aria-hidden="true" className="h-4 w-4" />Waiting on your care team about {program.pending.toDoseLabel}
              </p>
            )}
            {program.options.some(o => o.approved) && (
              <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#14532d]">
                <Check aria-hidden="true" className="h-4 w-4" />
                {program.options.filter(o => o.approved).map(o => o.doseLabel).join(', ')} approved. Ready to order.
              </p>
            )}
          </div>
          <button type="button" onClick={() => setOpen(program)}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">
            Change dose
          </button>
        </div>
      ))}
      {open && <DoseChangeSheet program={programs.find(p => p.programId === open.programId) || open} readOnly={readOnly} onClose={() => setOpen(null)} />}
    </section>
  );
}

function DoseChangeSheet({ program, readOnly, onClose }: { program: ProgramDoseChoices; readOnly: boolean; onClose: () => void }) {
  const { physicianId } = useDtpParams();
  const navigate = useNavigate();
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<DoseOption | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ask = useMutation({
    mutationFn: async (option: DoseOption) => {
      const res = await dtpApi.requestDoseChange(program.programId, option.doseLabel, note.trim() || undefined);
      if (!res.success) throw new Error(res.error || 'Could not send your request');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dose-choices'] });
    },
  });
  const askError = ask.error ? refillApiError(ask.error, 'We couldn’t send your request. Please try again.').message : '';

  function order(option: DoseOption) {
    const cart = useDtpCartStore.getState();
    // One dose of a program per order: drop any other dose of it first.
    const programProducts = new Set(program.options.map(o => o.productId));
    cart.items.filter(i => programProducts.has(i.productId)).forEach(i => cart.removeItem(i.productId));
    cart.addItem({
      productId: option.productId,
      productName: option.productName,
      price: option.price ?? 0,
      plan: { programId: program.programId, planId: option.planId, path: option.path },
    });
    trackStoreEvent('add_to_cart', { productId: option.productId, source: 'dose_change' });
    navigate(dtpPath(physicianId, '/checkout'));
  }

  return <div className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(36,26,21,0.4)] sm:items-center" onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={e => e.stopPropagation()}
      className="portal-card max-h-[92vh] w-full overflow-y-auto rounded-b-none bg-[var(--portal-card-solid)] p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-md sm:rounded-b-[inherit] sm:pb-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 id={titleId} className="portal-display text-2xl font-bold">{ask.isSuccess ? 'Request sent' : 'Change your dose'}</h2>
          <p className="mt-1 text-sm text-[var(--portal-muted)]">{program.programName}</p>
        </div>
        <button ref={closeButton} type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 shrink-0 place-items-center rounded-full hover:bg-white/80">
          <X aria-hidden="true" className="h-5 w-5" />
        </button>
      </header>

      {ask.isSuccess ? (
        <div className="mt-4 space-y-4">
          <p className="flex items-start gap-3 text-[15px]">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#dcf2e3] text-[#14532d]"><Check aria-hidden="true" className="h-4 w-4" /></span>
            <span>Your care team will review the change to <strong>{asking?.doseLabel}</strong> and reply in Messages. Once it’s approved, you can order it here.</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to={dtpPath(physicianId, '/messages')} className="inline-flex h-10 items-center rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">Open messages</Link>
            <button type="button" onClick={onClose} className="inline-flex h-10 items-center rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white">Done</button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-4 rounded-2xl bg-[var(--portal-tint-2)] p-4 text-[15px]">
            You can move one step up or down on your own. A bigger change goes to your care team first. Your prescriber reviews every order before it ships.
          </p>
          <ul className="mt-4 space-y-2">
            {program.options.map(option => {
              const isAsking = asking?.doseLabel === option.doseLabel;
              return <li key={option.doseLabel} className={`rounded-2xl p-4 ring-1 ${option.current ? 'bg-[var(--portal-tint-1)] ring-transparent' : 'bg-white/80 ring-[var(--portal-line)]'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold">{option.doseLabel}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-sm text-[var(--portal-muted)]">
                      {option.direction === 'up' && <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />}
                      {option.direction === 'down' && <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />}
                      {stepLabel(option)}{option.price !== undefined && ` · ${formatMoney(option.price)}`}
                    </p>
                  </div>
                  <DoseAction option={option} pendingFor={program.pending?.toDoseLabel} readOnly={readOnly}
                    onOrder={() => order(option)} onAsk={() => { setAsking(option); ask.reset(); }} asking={isAsking} />
                </div>
                {isAsking && (
                  <form className="mt-3 space-y-2" onSubmit={e => { e.preventDefault(); ask.mutate(option); }}>
                    <label htmlFor={`dose-note-${titleId}`} className="text-sm font-semibold">Anything your care team should know? (optional)</label>
                    <textarea id={`dose-note-${titleId}`} value={note} onChange={e => setNote(e.target.value)} maxLength={500} rows={3}
                      placeholder="For example: side effects, or how your weight has changed"
                      className="w-full rounded-2xl bg-white p-3 text-[15px] ring-1 ring-[var(--portal-line)] focus:outline-none focus:ring-2 focus:ring-[var(--portal-accent)]" />
                    {askError && <p role="alert" className="rounded-2xl bg-[#fde4dc] p-3 text-sm text-[#8c2a14]">{askError}</p>}
                    <button type="submit" disabled={ask.isPending}
                      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[var(--portal-primary)] text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90 disabled:opacity-50">
                      {ask.isPending && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Ask for {option.doseLabel}
                    </button>
                  </form>
                )}
              </li>;
            })}
          </ul>
          <p className="mt-4 text-xs text-[var(--portal-muted)]">An approved change can be ordered for {DOSE_APPROVAL_VALID_DAYS} days.</p>
        </>
      )}
    </div>
  </div>;
}

function DoseAction({ option, pendingFor, readOnly, asking, onOrder, onAsk }: {
  option: DoseOption; pendingFor?: string; readOnly: boolean; asking: boolean; onOrder: () => void; onAsk: () => void;
}) {
  const button = 'inline-flex h-10 shrink-0 items-center rounded-full px-4 text-sm font-semibold disabled:opacity-50';
  if (!option.available) return <span className="shrink-0 text-sm text-[var(--portal-muted)]">Not available</span>;
  if (option.needsApproval) {
    if (pendingFor === option.doseLabel) return <span className="flex shrink-0 items-center gap-1 text-sm text-[var(--portal-muted)]"><Clock aria-hidden="true" className="h-4 w-4" />Requested</span>;
    if (asking) return null;
    return <button type="button" onClick={onAsk} disabled={readOnly || !!pendingFor}
      title={pendingFor ? `You already asked for ${pendingFor}` : undefined}
      className={`${button} bg-white ring-1 ring-[var(--portal-line)] hover:bg-white/80`}>Ask care team</button>;
  }
  return <button type="button" onClick={onOrder} disabled={readOnly}
    className={`${button} bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] hover:opacity-90`}>
    {option.approved ? 'Approved · Order' : option.current ? 'Order again' : 'Order this dose'}
  </button>;
}
